import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  ConsultOrderPrefillResponse,
  CncOrderDeliveryPaymentType,
  PersonalIdType,
  UserAddressRequestPayload,
} from "../types/consultOrder.types";
import { ItemInMarketplaceBasicResponse } from "../types/marketplaceItemSearch.types";
import { CONSULT_LOYALTY_PAYMENT_ENABLED } from "../utils/consultOrder.constants";

// ITAM-2583: owns the caller PII (prefill) and the in-memory order DRAFT for the
// current consult. Kept out of Redux and out of browser storage (privacy posture).
// Every consult-order form component reads/writes the draft THROUGH this context,
// so the components stay decoupled from one another (the context is the shared bus).

export type ConsultOrderFulfillment =
  | "PICKUP"
  | "STANDARD_DELIVERY"
  | "FAST_DELIVERY";

export type ConsultOrderAddressMode = "SAVED" | "NEW";

/**
 * Result of the last delivery zone pre-check, tagged with the selection KEY
 * (delivery type + resolved coordinates) it was computed for. Validation compares
 * this key against the CURRENT selection so a stale approval (address or delivery
 * type changed since the check) never counts as verified — it blocks until a
 * fresh check for the new selection completes.
 *   inZone: true  -> verified in zone   (allow)
 *   inZone: false -> verified out of zone (block)
 *   inZone: null  -> could not verify (403 / missing marketplace coords) -> degrade
 */
export interface DeliveryZoneCheck {
  key: string;
  inZone: boolean | null;
}

/**
 * Resolved branch delivery fee for the current fulfillment, mirrored from
 * DeliverySection (which fetches the branch SERVICE items) and tagged with the
 * delivery type it was resolved for. Validation recomputes the key from the
 * CURRENT fulfillment so a fee resolved for a different type never counts, and
 * the route-independent lifecycle submit() reads `serviceItemId`/`price` to build
 * the payload WITHOUT depending on the nullable marketplaceInfoDetailed fee.
 *   status "ok"        -> price is a usable number (incl. a genuine 0) -> allow
 *   status "loading"   -> the SERVICE items are still being fetched   -> block
 *   "missing"/"duplicate"/"malformed"/"error" -> no usable fee        -> block
 */
export type DeliveryServiceFeeStatus =
  | "loading"
  | "ok"
  | "missing"
  | "duplicate"
  | "malformed"
  | "error";

export interface DeliveryServiceFee {
  key: string; // the delivery fulfillment type it was resolved for
  status: DeliveryServiceFeeStatus;
  serviceItemId: number | null;
  price: number | null; // usable number (incl. 0) only when status === "ok"
}

export interface ConsultOrderLine {
  item: ItemInMarketplaceBasicResponse;
  quantity: number;
}

export interface ConsultOrderDraft {
  items: ConsultOrderLine[];
  fulfillment: ConsultOrderFulfillment;

  // Delivery-only (ignored + excluded from payload/validation for PICKUP)
  addressMode: ConsultOrderAddressMode;
  selectedAddressId: number | null; // saved-address id
  newAddress: UserAddressRequestPayload | null; // Places-resolved (phase-2)
  saveNewAddress: boolean; // always an explicit boolean; default false
  paymentType: CncOrderDeliveryPaymentType;
  personalIdType: PersonalIdType;
  personalId: string;
  deliveryTimeSlot: { start: string; end: string } | null; // STANDARD only
  noteForDelivery: string;
  // Latest delivery zone pre-check, mirrored from DeliverySection and tagged with
  // the selection it was computed for, so the route-independent lifecycle submit()
  // can hard-block a confirmed out-of-zone address AND reject a stale approval
  // whose selection has since changed. See DeliveryZoneCheck.
  deliveryZoneCheck?: DeliveryZoneCheck | null;
  // Latest resolved branch delivery (SERVICE) fee, mirrored from DeliverySection
  // and keyed to the fulfillment. See DeliveryServiceFee.
  deliveryServiceFee?: DeliveryServiceFee | null;

  // Customer (prefilled, editable)
  firstName: string;
  secondName: string;
  phoneNumber: string;
  note: string;
}

// ITAM-2614: the consult RFZO therapy draft. A SEPARATE field on the same
// context (never merged into the CnC order draft) so the pharmacist can switch
// between the two modes through the prompt while both stay intact. Same privacy
// posture: memory only, never Redux / browser storage.
export interface ConsultRfzoDraftItem {
  itemId: number;
  /** Display only — the payload sends itemId alone (never externalId, D5). */
  name: string;
  rfzoQuantity: number;
  rfzoParticipationPrice: number;
}

export interface ConsultRfzoDraft {
  lbo: string;
  // ITAM-2716: the pharmacist may identify the patient by LBO, by JMBG, or by
  // both; the backend requires at least one. Never prefilled: the consult API
  // carries no therapy JMBG, and the delivery-document personalId is a
  // different field with a different meaning.
  jmbg: string;
  // D11: order customer identity is captured at step 1 (the order step cannot
  // change it), so the RFZO form owns these fields, prefilled from the consult.
  firstName: string;
  secondName: string;
  userPhone: string;
  items: ConsultRfzoDraftItem[];
  willBeReady: Date | null;
  lastTherapyDate: Date | null;
  noteForUser: string;
  noteForMarketplace: string;
}

export interface ConsultOrderContextValue {
  prefill: ConsultOrderPrefillResponse | null;
  draft: ConsultOrderDraft;
  rfzoDraft: ConsultRfzoDraft;
  hydrate: (prefill: ConsultOrderPrefillResponse) => void;
  updateDraft: (patch: Partial<ConsultOrderDraft>) => void;
  addItem: (item: ItemInMarketplaceBasicResponse) => void;
  setItemQuantity: (itemId: number, quantity: number) => void;
  removeItem: (itemId: number) => void;
  updateRfzoDraft: (patch: Partial<ConsultRfzoDraft>) => void;
  addRfzoItem: (item: ConsultRfzoDraftItem) => void;
  removeRfzoItem: (itemId: number) => void;
  clearDraft: () => void;
}

const FE_PERSONAL_ID_TYPES: PersonalIdType[] = [
  "JMBG",
  "PERSONAL_CARD_NUMBER",
  "PASSPORT_NUMBER",
];

const DEFAULT_PERSONAL_ID_TYPE: PersonalIdType = "PERSONAL_CARD_NUMBER";

/** Estimated per-unit price: discounted branch price when present, else base. */
export const lineUnitPrice = (item: ItemInMarketplaceBasicResponse): number =>
  item.priceWithDiscount ?? item.price;

/** Estimated items subtotal from the DISCOUNTED branch price (201 is authoritative). */
export const estimatedItemsSubtotal = (lines: ConsultOrderLine[]): number =>
  lines.reduce((sum, line) => sum + lineUnitPrice(line.item) * line.quantity, 0);

const emptyDraft = (): ConsultOrderDraft => ({
  items: [],
  fulfillment: "PICKUP",
  addressMode: "SAVED",
  selectedAddressId: null,
  newAddress: null,
  saveNewAddress: false,
  // Loyalty is hidden (ITAM-2756/2757) -> CASH is the only offered method, so
  // it must also be the default a fresh draft carries into the payload.
  paymentType: CONSULT_LOYALTY_PAYMENT_ENABLED ? "LOYALTY_POINTS" : "CASH",
  personalIdType: DEFAULT_PERSONAL_ID_TYPE,
  personalId: "",
  deliveryTimeSlot: null,
  noteForDelivery: "",
  deliveryZoneCheck: null,
  deliveryServiceFee: null,
  firstName: "",
  secondName: "",
  phoneNumber: "",
  note: "",
});

const emptyRfzoDraft = (): ConsultRfzoDraft => ({
  lbo: "",
  jmbg: "",
  firstName: "",
  secondName: "",
  userPhone: "",
  items: [],
  willBeReady: null,
  lastTherapyDate: null,
  noteForUser: "",
  noteForMarketplace: "",
});

const rfzoDraftFromPrefill = (
  prefill: ConsultOrderPrefillResponse
): ConsultRfzoDraft => {
  const user = prefill.user;
  return {
    ...emptyRfzoDraft(),
    lbo: user.lbo ?? "",
    firstName: user.firstName ?? "",
    secondName: user.secondName ?? "",
    userPhone: user.phoneNumber ?? "",
  };
};

const draftFromPrefill = (
  prefill: ConsultOrderPrefillResponse
): ConsultOrderDraft => {
  const user = prefill.user;
  const prefillType =
    user.personalIdType && FE_PERSONAL_ID_TYPES.includes(user.personalIdType)
      ? user.personalIdType
      : DEFAULT_PERSONAL_ID_TYPE;
  return {
    ...emptyDraft(),
    personalIdType: prefillType,
    personalId: user.personalId ?? "",
    firstName: user.firstName ?? "",
    secondName: user.secondName ?? "",
    phoneNumber: user.phoneNumber ?? "",
  };
};

const ConsultOrderContext = createContext<ConsultOrderContextValue | undefined>(
  undefined
);

export const ConsultOrderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [prefill, setPrefill] = useState<ConsultOrderPrefillResponse | null>(
    null
  );
  const [draft, setDraft] = useState<ConsultOrderDraft>(emptyDraft);
  const [rfzoDraft, setRfzoDraft] = useState<ConsultRfzoDraft>(emptyRfzoDraft);

  const hydrate = useCallback((next: ConsultOrderPrefillResponse) => {
    setPrefill(next);
    setDraft(draftFromPrefill(next));
    setRfzoDraft(rfzoDraftFromPrefill(next));
  }, []);

  const updateDraft = useCallback((patch: Partial<ConsultOrderDraft>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const addItem = useCallback((item: ItemInMarketplaceBasicResponse) => {
    setDraft((prev) => {
      const idx = prev.items.findIndex(
        (line) => line.item.id.itemId === item.id.itemId
      );
      if (idx >= 0) {
        // Re-add merges: bump quantity, capped at the FRESH stock, and adopt
        // the fresh item snapshot (price/stock) — the stock-overview modal
        // refetches on every open, so the incoming item is never staler than
        // the stored one. When stock dropped below the stored quantity this
        // clamps DOWN (the modal warns before the click).
        const existing = prev.items[idx];
        const nextQty = Math.min(existing.quantity + 1, item.quantity);
        const items = [...prev.items];
        items[idx] = { item, quantity: nextQty };
        return { ...prev, items };
      }
      return { ...prev, items: [...prev.items, { item, quantity: 1 }] };
    });
  }, []);

  const setItemQuantity = useCallback((itemId: number, quantity: number) => {
    setDraft((prev) => {
      const items = prev.items.map((line) => {
        if (line.item.id.itemId !== itemId) {
          return line;
        }
        // Clamp to [1, stock].
        const clamped = Math.max(1, Math.min(quantity, line.item.quantity));
        return { item: line.item, quantity: clamped };
      });
      return { ...prev, items };
    });
  }, []);

  const removeItem = useCallback((itemId: number) => {
    setDraft((prev) => ({
      ...prev,
      items: prev.items.filter((line) => line.item.id.itemId !== itemId),
    }));
  }, []);

  const updateRfzoDraft = useCallback((patch: Partial<ConsultRfzoDraft>) => {
    setRfzoDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const addRfzoItem = useCallback((item: ConsultRfzoDraftItem) => {
    setRfzoDraft((prev) => {
      const idx = prev.items.findIndex((line) => line.itemId === item.itemId);
      if (idx >= 0) {
        // Duplicates merge by itemId: the newest entry (quantity/participation)
        // wins — re-adding an item is how the pharmacist corrects a line.
        const items = [...prev.items];
        items[idx] = item;
        return { ...prev, items };
      }
      return { ...prev, items: [...prev.items, item] };
    });
  }, []);

  const removeRfzoItem = useCallback((itemId: number) => {
    setRfzoDraft((prev) => ({
      ...prev,
      items: prev.items.filter((line) => line.itemId !== itemId),
    }));
  }, []);

  const clearDraft = useCallback(() => {
    setPrefill(null);
    setDraft(emptyDraft());
    setRfzoDraft(emptyRfzoDraft());
  }, []);

  const value = useMemo<ConsultOrderContextValue>(
    () => ({
      prefill,
      draft,
      rfzoDraft,
      hydrate,
      updateDraft,
      addItem,
      setItemQuantity,
      removeItem,
      updateRfzoDraft,
      addRfzoItem,
      removeRfzoItem,
      clearDraft,
    }),
    [
      prefill,
      draft,
      rfzoDraft,
      hydrate,
      updateDraft,
      addItem,
      setItemQuantity,
      removeItem,
      updateRfzoDraft,
      addRfzoItem,
      removeRfzoItem,
      clearDraft,
    ]
  );

  return (
    <ConsultOrderContext.Provider value={value}>
      {children}
    </ConsultOrderContext.Provider>
  );
};

export const useConsultOrderContext = (): ConsultOrderContextValue => {
  const ctx = useContext(ConsultOrderContext);
  if (!ctx) {
    throw new Error(
      "useConsultOrderContext must be used within a ConsultOrderProvider"
    );
  }
  return ctx;
};
